import React from 'react';
import { Helmet } from 'react-helmet-async';

const Terms = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Terms of Service - Trademark Inspire | Legal Terms & Conditions for Trademark Services</title>
        <meta name="description" content="Read Trademark Inspire's terms of service and conditions. Understand your rights and obligations when using our trademark registration and intellectual property services. Updated January 2026." />
        <meta name="keywords" content="terms of service, legal terms, service agreement, terms and conditions, trademark service terms, user agreement" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#093336] via-[#0a4548] to-[#093336] text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
            <p className="text-xl text-gray-100">Last Updated: January 2026</p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">1. Agreement to Terms</h2>
                <p className="text-gray-700">
                  By accessing or using the Trademark Inspire website and services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this site and our services.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">2. Description of Services</h2>
                <p className="text-gray-700">
                  Trademark Inspire provides trademark registration services, legal consultation, copyright services, patent services, company formation services, and related intellectual property services. We act as intermediaries to facilitate the trademark application process with the United States Patent and Trademark Office (USPTO) and other relevant authorities.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">3. User Responsibilities</h2>
                <p className="text-gray-700 mb-3">
                  As a user of our services, you agree to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Provide accurate, current, and complete information during the registration process</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Notify us immediately of any unauthorized use of your account</li>
                  <li>Use our services only for lawful purposes and in accordance with these Terms</li>
                  <li>Not use our services to infringe on the intellectual property rights of others</li>
                  <li>Not attempt to interfere with the proper working of our services</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">4. Fees and Payment</h2>
                <p className="text-gray-700">
                  Our service fees are clearly outlined in our pricing packages. Government filing fees are separate and in addition to our service fees. All fees are non-refundable once the trademark application has been filed with the USPTO, except as required by law or as explicitly stated in our refund policy. We reserve the right to change our fees at any time, but such changes will not affect orders already placed.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">5. Intellectual Property Rights</h2>
                <p className="text-gray-700">
                  The content, features, and functionality of our website are owned by Trademark Inspire and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws. You may not reproduce, distribute, modify, create derivative works of, publicly display, or exploit for any commercial purposes any portion of our services without our express written permission.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">6. Disclaimer of Warranties</h2>
                <p className="text-gray-700">
                  While we strive for accuracy and quality in our services, we cannot guarantee approval of your trademark application by the USPTO. The services are provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that the services will be uninterrupted, error-free, or free of viruses or other harmful components.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">7. Limitation of Liability</h2>
                <p className="text-gray-700">
                  To the fullest extent permitted by applicable law, Trademark Inspire shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your use of our services.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">8. Indemnification</h2>
                <p className="text-gray-700">
                  You agree to defend, indemnify, and hold harmless Trademark Inspire and its affiliates, officers, directors, employees, and agents from and against any claims, damages, obligations, losses, liabilities, costs, or debt, and expenses arising from your use of our services, your violation of these Terms, or your violation of any third-party rights.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">9. Termination</h2>
                <p className="text-gray-700">
                  We may terminate or suspend your access to our services immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach these Terms. Upon termination, your right to use the services will immediately cease.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">10. Governing Law</h2>
                <p className="text-gray-700">
                  These Terms shall be governed by and construed in accordance with the laws of the United States and the State of New York, without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of our services shall be resolved in the courts located in New York, NY.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">11. Changes to Terms</h2>
                <p className="text-gray-700">
                  We reserve the right to modify or replace these Terms at any time at our sole discretion. We will provide notice of any material changes by posting the new Terms on this page and updating the "Last Updated" date. Your continued use of our services after any such changes constitutes your acceptance of the new Terms.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">12. Contact Information</h2>
                <p className="text-gray-700">
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <ul className="list-none space-y-2 text-gray-700 mt-3">
                  <li><strong>Email:</strong> Info@trademarkinspire.com</li>
                  <li><strong>Phone:</strong> +1 (888) 2029 517</li>
                  <li><strong>Address:</strong> 1652 W Texas St, Fairfield, CA 94533, United States</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
